import React, { useState } from 'react';
import { Package, RefreshCw, X, AlertCircle, Download } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getWooCommerceProducts, syncProductToWooCommerce, unlinkWooCommerceProduct } from '../../services/woocommerce';
import { ProductWithWooCommerce, WooCommerceProduct } from '../../types/woocommerce';
import { WooCommerceConfig } from '../../types/integrations';
import { updateDoc, doc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import WooCommerceImportModal from './WooCommerceImportModal';

interface WooCommerceProductSyncProps {
  product: ProductWithWooCommerce;
  config: WooCommerceConfig;
  onSyncComplete?: () => void;
}

export default function WooCommerceProductSync({ product, config, onSyncComplete }: WooCommerceProductSyncProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const { currentUser } = useAuth();

  const handleSync = async () => {
    if (!currentUser?.companyId) return;
    
    try {
      setLoading(true);
      setError('');

      // Sync product to WooCommerce
      const wooCommerceProduct = await syncProductToWooCommerce(
        {
          url: config.url,
          consumerKey: config.consumerKey,
          consumerSecret: config.consumerSecret
        },
        product
      );

      // Update product in Firebase with WooCommerce link
      await updateDoc(doc(db, 'products', product.id), {
        woocommerce: {
          woocommerce_id: wooCommerceProduct.id,
          store_name: 'WooCommerce Store', // You might want to make this configurable
          store_url: config.url,
          last_synced: new Date().toISOString()
        }
      });

      onSyncComplete?.();
    } catch (err) {
      console.error('Error syncing product:', err);
      setError(err instanceof Error ? err.message : 'Failed to sync product');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlink = async () => {
    if (!currentUser?.companyId || !product.woocommerce?.woocommerce_id) return;
    
    try {
      setLoading(true);
      setError('');

      // Delete product from WooCommerce
      await unlinkWooCommerceProduct(
        {
          url: config.url,
          consumerKey: config.consumerKey,
          consumerSecret: config.consumerSecret
        },
        product.woocommerce.woocommerce_id
      );

      // Remove WooCommerce link from product in Firebase
      await updateDoc(doc(db, 'products', product.id), {
        woocommerce: null
      });

      onSyncComplete?.();
    } catch (err) {
      console.error('Error unlinking product:', err);
      setError(err instanceof Error ? err.message : 'Failed to unlink product');
    } finally {
      setLoading(false);
    }
  };

  const handleProductSelect = async (wooProduct: WooCommerceProduct) => {
    if (!currentUser?.companyId) return;
    
    try {
      setLoading(true);
      setError('');

      // Update product in Firebase with WooCommerce link
      await updateDoc(doc(db, 'products', product.id), {
        woocommerce: {
          woocommerce_id: wooProduct.id,
          store_name: 'WooCommerce Store',
          store_url: config.url,
          last_synced: new Date().toISOString()
        }
      });

      onSyncComplete?.();
    } catch (err) {
      console.error('Error linking product:', err);
      setError(err instanceof Error ? err.message : 'Failed to link product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {product.woocommerce ? (
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            <Package className="text-primary" size={20} />
            <div>
              <p className="font-medium">Connected to WooCommerce</p>
              <p className="text-sm text-gray-500">
                Last synced: {new Date(product.woocommerce.last_synced).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSync}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary text-white rounded hover:bg-primary-dark disabled:opacity-50"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Sync
            </button>
            <button
              onClick={handleUnlink}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded hover:bg-red-100 disabled:opacity-50"
            >
              <X size={16} />
              Unlink
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <button
            onClick={handleSync}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark disabled:opacity-50"
          >
            <Package size={20} />
            Connect to WooCommerce
          </button>
          <button
            onClick={() => setIsImportModalOpen(true)}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
          >
            <Download size={20} />
            Import from WooCommerce
          </button>
        </div>
      )}

      <WooCommerceImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        config={config}
        onProductSelect={handleProductSelect}
      />
    </div>
  );
}
